import { injectable, inject } from 'inversify';
import { GTD_DOMAIN_SYMBOLS } from '../../../../infrastructure/di/GTDDomainSymbols';
import { z } from 'zod';
import { NextAction } from '../../domain/aggregates/NextAction';
import { ActionContext, EnergyLevel } from '../../domain/value-objects';
import { NextActionAggregateRepository } from '../../infrastructure/repositories/NextActionAggregateRepository';
import { BaseApplicationError } from '@swoft/core';

/**
 * Command Schemas with Zod Validation
 */
export const CreateNextActionCommandSchema = z.object({
  description: z.string().min(1, 'Action description cannot be empty').max(500, 'Description too long'),
  context: z.enum(['@calls', '@computer', '@errands', '@home', '@office', '@anywhere']),
  energyLevel: z.enum(['high', 'medium', 'low']),
  estimatedMinutes: z.number().min(1, 'Estimated time must be at least 1 minute').max(480, 'Estimated time cannot exceed 8 hours'),
  projectId: z.string().optional(),
  createdBy: z.string().min(1, 'Created by is required')
}).strict();

export const AssignActionCommandSchema = z.object({
  actionId: z.string().min(1, 'Action ID is required'),
  assignedTo: z.string().min(1, 'Assigned to is required'),
  roleType: z.string().min(1, 'Role type is required')
}).strict();

export const CompleteActionCommandSchema = z.object({
  actionId: z.string().min(1, 'Action ID is required')
}).strict();

// Command Types
export type CreateNextActionCommand = z.infer<typeof CreateNextActionCommandSchema>;
export type AssignActionCommand = z.infer<typeof AssignActionCommandSchema>;
export type CompleteActionCommand = z.infer<typeof CompleteActionCommandSchema>;

// Result Types
export interface CreateNextActionResult {
  readonly actionId: string;
  readonly success: boolean;
  readonly message: string;
}

export interface ActionOperationResult {
  readonly success: boolean;
  readonly message: string;
}

// Error classes
export class InvalidActionCommandError extends BaseApplicationError {
  readonly code = 'INVALID_ACTION_COMMAND';
  
  constructor(validationErrors: string[]) {
    super(
      'Invalid action command data',
      {
        ux: [
          'Please check the following and try again:',
          ...validationErrors
        ],
        dx: [`Command validation failed: ${validationErrors.join(', ')}`]
      }
    );
  }
}

export class ActionNotFoundError extends BaseApplicationError {
  readonly code = 'ACTION_NOT_FOUND';
  
  constructor(actionId: string) {
    super(
      `Next action not found: ${actionId}`,
      {
        ux: [
          'The requested action could not be found.',
          'It may have been deleted or the ID is incorrect.'
        ],
        dx: [`Action lookup failed for ID: ${actionId}`]
      }
    );
  }
}

/**
 * NextActionWriteService - Command Side (CQRS)
 * 
 * Handles all write operations for GTD next actions.
 * Implements the GTD principle of capturing and organizing actionable items.
 */
@injectable()
export class NextActionWriteService {
  constructor(
    @inject(GTD_DOMAIN_SYMBOLS.NextActionAggregateRepository)
    private readonly repository: NextActionAggregateRepository
  ) {}

  /**
   * Create a new next action
   * Core GTD principle: Capture actionable items with context and energy
   */
  async createNextAction(command: CreateNextActionCommand): Promise<CreateNextActionResult> {
    // 1. Command validation
    let validatedCommand: CreateNextActionCommand;
    try {
      validatedCommand = CreateNextActionCommandSchema.parse(command);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new InvalidActionCommandError(error.errors.map(e => e.message));
      }
      throw error;
    }

    try {
      // 2. Create domain objects
      const context = this.parseContext(validatedCommand.context);
      const energy = this.parseEnergyLevel(validatedCommand.energyLevel);

      // 3. Create aggregate
      const action = NextAction.create(
        validatedCommand.description,
        context,
        energy,
        validatedCommand.estimatedMinutes,
        validatedCommand.createdBy,
        validatedCommand.projectId
      );

      // 4. Persist
      await this.repository.save(action);

      return {
        actionId: action.id,
        success: true,
        message: 'Next action created successfully'
      };

    } catch (error) {
      return {
        actionId: '',
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create action'
      };
    }
  }

  /**
   * Assign action to a person
   * Integrates with Party Management for assignment
   */
  async assignAction(command: AssignActionCommand): Promise<ActionOperationResult> {
    // 1. Command validation
    let validatedCommand: AssignActionCommand;
    try {
      validatedCommand = AssignActionCommandSchema.parse(command);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new InvalidActionCommandError(error.errors.map(e => e.message));
      }
      throw error;
    }

    // 2. Load action
    const action = await this.repository.findById(validatedCommand.actionId);
    if (!action) {
      throw new ActionNotFoundError(validatedCommand.actionId);
    }

    try {
      // 3. Perform assignment
      action.assignTo(validatedCommand.assignedTo, validatedCommand.roleType);

      // 4. Persist
      await this.repository.save(action);

      return {
        success: true,
        message: 'Action assigned successfully'
      };

    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to assign action'
      };
    }
  }

  /**
   * Complete an action
   * Core GTD principle: Mark actions as done when completed
   */
  async completeAction(command: CompleteActionCommand): Promise<ActionOperationResult> {
    // 1. Command validation
    let validatedCommand: CompleteActionCommand;
    try {
      validatedCommand = CompleteActionCommandSchema.parse(command);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new InvalidActionCommandError(error.errors.map(e => e.message));
      }
      throw error;
    }

    // 2. Load action
    const action = await this.repository.findById(validatedCommand.actionId);
    if (!action) {
      throw new ActionNotFoundError(validatedCommand.actionId);
    }

    try {
      // 3. Complete action
      action.complete();

      // 4. Persist
      await this.repository.save(action);

      return {
        success: true,
        message: 'Action completed successfully'
      };

    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to complete action'
      };
    }
  }

  /**
   * Delete an action
   */
  async deleteAction(actionId: string): Promise<ActionOperationResult> {
    try {
      const action = await this.repository.findById(actionId);
      if (!action) {
        throw new ActionNotFoundError(actionId);
      }

      await this.repository.delete(actionId);

      return {
        success: true,
        message: 'Action deleted successfully'
      };

    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to delete action'
      };
    }
  }

  // Helper methods
  private parseContext(context: string): ActionContext {
    switch (context) {
      case '@calls': return ActionContext.atCalls();
      case '@computer': return ActionContext.atComputer();
      case '@errands': return ActionContext.atErrands('');
      case '@home': return ActionContext.atHome();
      case '@office': return ActionContext.atOffice();
      case '@anywhere': return ActionContext.anywhere();
      default: return ActionContext.atComputer();
    }
  }

  private parseEnergyLevel(energy: string): EnergyLevel {
    switch (energy) {
      case 'high': return EnergyLevel.high();
      case 'medium': return EnergyLevel.medium();
      case 'low': return EnergyLevel.low();
      default: return EnergyLevel.medium();
    }
  }
}